import { HERO_ORDER, getHeroDef } from './heroes.ts';
import { STRUCTURE_LAYOUT, heroSpawn } from './lane.ts';
import { Simulation } from './simulation.ts';
import type { HeroId, Team } from './types.ts';

/**
 * Modos de partida:
 * - `lanes`: 3v3 en tres líneas (un héroe por línea).
 * - `aram`: una sola línea (la central) para 1v1, 2v2 o 3v3.
 */
export type MatchMode = 'lanes' | 'aram';

export interface MatchConfig {
  mode: MatchMode;
  /** héroes por equipo (en `lanes` siempre 3) */
  teamSize: number;
}

/** Configuración + alineación: con esto cliente y servidor construyen la misma partida. */
export interface MatchSetup {
  config: MatchConfig;
  roster: Record<Team, HeroId[]>;
}

export const DEFAULT_MATCH_CONFIG: MatchConfig = { mode: 'lanes', teamSize: 3 };
export const ARAM_TEAM_SIZES = [1, 2, 3];
export const TEAMS: Team[] = ['blue', 'red'];

/** Líneas activas de cada modo. */
export function matchLanes(config: MatchConfig): number[] {
  return config.mode === 'aram' ? [1] : [0, 1, 2];
}

/** Valida una configuración que viene de fuera (lobby, red). */
export function sanitizeMatchConfig(raw: unknown): MatchConfig {
  const source = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  if (source.mode === 'aram') {
    const size = Math.floor(Number(source.teamSize));
    return { mode: 'aram', teamSize: ARAM_TEAM_SIZES.includes(size) ? size : 3 };
  }
  return { ...DEFAULT_MATCH_CONFIG };
}

export function isHeroId(value: unknown): value is HeroId {
  return typeof value === 'string' && (HERO_ORDER as string[]).includes(value);
}

/** Nombre corto del modo para la interfaz. */
export function matchLabel(config: MatchConfig): string {
  const size = `${config.teamSize}v${config.teamSize}`;
  return config.mode === 'aram' ? `ARAM ${size}` : `${size} · Tres líneas`;
}

/** Tríos por defecto; el jugador siempre juega en azul con el trío de su héroe. */
export const DEFAULT_TEAMS: Record<Team, HeroId[]> = {
  blue: ['knight', 'mage', 'falco'],
  red: ['barbarian', 'mario', 'sheik'],
};

/**
 * Equipos 3v3 para el héroe elegido: juega en azul, línea central, con los otros
 * dos de su trío; el trío opuesto es el rival.
 */
export function brawlRoster(playerHero: HeroId): Record<Team, HeroId[]> {
  const ownTrio = DEFAULT_TEAMS.blue.includes(playerHero) ? DEFAULT_TEAMS.blue : DEFAULT_TEAMS.red;
  const otherTrio = ownTrio === DEFAULT_TEAMS.blue ? DEFAULT_TEAMS.red : DEFAULT_TEAMS.blue;
  return {
    blue: [playerHero, ...ownTrio.filter((id) => id !== playerHero)],
    red: [...otherTrio],
  };
}

/** Alineación por defecto para un modo: los primeros `teamSize` de cada trío. */
export function defaultRoster(config: MatchConfig, playerHero: HeroId = 'knight'): Record<Team, HeroId[]> {
  const full = brawlRoster(playerHero);
  return { blue: full.blue.slice(0, config.teamSize), red: full.red.slice(0, config.teamSize) };
}

/**
 * Completa la alineación con bots: cada equipo lleva primero los héroes elegidos
 * por sus jugadores y rellena con héroes que no estén ya en ese equipo
 * (preferentemente los de su trío por defecto).
 */
export function fillRoster(config: MatchConfig, picks: Record<Team, HeroId[]>): Record<Team, HeroId[]> {
  const out = {} as Record<Team, HeroId[]>;
  for (const team of TEAMS) {
    const chosen = picks[team].slice(0, config.teamSize);
    const preferred = [...DEFAULT_TEAMS[team], ...HERO_ORDER];
    for (const heroId of preferred) {
      if (chosen.length >= config.teamSize) break;
      const taken = chosen.includes(heroId) || TEAMS.some((t) => t !== team && picks[t].includes(heroId));
      if (!taken) chosen.push(heroId);
    }
    // con todos los héroes ocupados se permiten repetidos entre equipos
    for (const heroId of preferred) {
      if (chosen.length >= config.teamSize) break;
      if (!chosen.includes(heroId)) chosen.push(heroId);
    }
    out[team] = chosen;
  }
  return out;
}

/**
 * Construye una partida a partir de su configuración y alineación. El orden de
 * creación (y por tanto los ids) es determinista: el cliente online crea la
 * misma simulación que el servidor y luego aplica sus snapshots.
 * `player`: puesto del jugador local (offline); `null` = todos son bots
 * (servidor, que luego activa los puestos de los jugadores).
 */
export function createMatchSimulation(setup: MatchSetup, player: { team: Team; slot: number } | null = null): Simulation {
  const config = sanitizeMatchConfig(setup.config);
  const sim = new Simulation();
  // se gana destruyendo el nexo; sin límite de tiempo (0 = sin reloj)
  sim.enableMatch({ durationTicks: 0, scoreToWin: 0 });
  sim.progression = true;

  // línea de cada puesto: el primero va al centro; en ARAM todos al centro
  const laneOf = config.mode === 'aram' ? [1, 1, 1] : [1, 0, 2];
  for (const team of TEAMS) {
    setup.roster[team].slice(0, config.teamSize).forEach((heroId, slot) => {
      const isPlayer = player !== null && player.team === team && player.slot === slot;
      const { x, z } = heroSpawn(team, slot);
      sim.addHero(heroId, {
        team,
        x,
        z,
        isPlayer,
        isBot: !isPlayer,
        name: isPlayer ? 'You' : `${getHeroDef(heroId).displayName} Bot`,
        lane: laneOf[slot],
        facing: team === 'blue' ? 0 : Math.PI,
      });
    });
  }
  sim.enableLane(matchLanes(config));

  // con los tríos invertidos, las estatuas de cada base pasan a ser las de su equipo
  const lead = setup.roster.blue[0];
  if (lead && !DEFAULT_TEAMS.blue.includes(lead)) {
    for (const structure of sim.structures) {
      const mirror = STRUCTURE_LAYOUT.find(
        (spec) =>
          spec.team !== structure.team &&
          spec.kind === structure.kind &&
          spec.tier === structure.tier &&
          spec.lane === structure.lane,
      );
      if (mirror) structure.model = mirror.model;
    }
  }
  return sim;
}

/** 3v3 en tres líneas. Por defecto: azul = Link (jugador), Samus, Falco · rojo = Bowser, Mario, Sheik. */
export function createBrawlSimulation(playerHero: HeroId = 'knight'): Simulation {
  return createMatchSimulation({ config: DEFAULT_MATCH_CONFIG, roster: brawlRoster(playerHero) }, { team: 'blue', slot: 0 });
}
